import React, { useState, useEffect } from 'react';
import { 
  DocumentTextIcon, 
  CogIcon, 
  PlusIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CloudArrowUpIcon,
  UsersIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import Layout from './Layout';
import DocumentSettingsModal from './DocumentSettingsModal';
import RequestDocumentsModal from './RequestDocumentsModal';
import BulkRequestDocumentsModal from './BulkRequestDocumentsModal';
import DocumentRequestDetail from './DocumentRequestDetail';
import ManualDocumentUploadModal from './ManualDocumentUploadModal';
import BulkManualDocumentUploadModal from './BulkManualDocumentUploadModal';
import DirectDocumentUploadModal from './DirectDocumentUploadModal';
import { useBranch } from '../contexts/BranchContext';
import api from '../services/api';
import PermissionGate from './PermissionGate';

interface DocumentRequestItem {
  id: number;
  requestId: number;
  documentTypeId: number;
  status: 'pending' | 'submitted' | 'accepted' | 'rejected';
  submittedAt?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  documentType: {
    name: string;
    description?: string;
    isRequired: boolean;
    maxFileSizeMb: number;
    allowedFileTypes: string[];
  };
}

interface DocumentRequest {
  id: number;
  organizationId: number;
  leadId: number;
  requestToken: string;
  status: 'requested' | 'submitted' | 'reviewed' | 'rejected';
  requestedBy: number;
  requestedAt: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: number;
  reviewNotes?: string;
  isReRequest?: boolean;
  createdAt: string;
  updatedAt: string;
  lead: {
    firstName: string;
    lastName: string;
    email: string;
  };
  requestedByUser: {
    firstName: string;
    lastName: string;
  };
  reviewedByUser?: {
    firstName: string;
    lastName: string;
  };
  items: DocumentRequestItem[];
}

const Documents: React.FC = () => {
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [requests, setRequests] = useState<DocumentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showBulkRequestModal, setShowBulkRequestModal] = useState(false);
  const [showRequestDropdown, setShowRequestDropdown] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<DocumentRequest | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showManualUploadModal, setShowManualUploadModal] = useState(false);
  const [selectedRequestForUpload, setSelectedRequestForUpload] = useState<DocumentRequest | null>(null);
  const [showBulkManualUploadModal, setShowBulkManualUploadModal] = useState(false);
  const [showDirectUploadModal, setShowDirectUploadModal] = useState(false);

  useEffect(() => {
    if (!branchLoading) {
      fetchRequests();
    }
  }, [currentPage, statusFilter, currentBranch, isViewingAllBranches, branchLoading]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });
      
      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await api.get(`/documents/requests?${params}`);
      
      setRequests(response.data.data || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (err: any) {
      console.error('Error fetching document requests:', err);
      setError('Failed to fetch document requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCreated = () => {
    setShowRequestModal(false);
    fetchRequests();
  };

  const handleRequestUpdated = () => {
    fetchRequests();
    setSelectedRequest(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'requested':
        return <ClockIcon className="w-4 h-4" />;
      case 'submitted':
        return <DocumentTextIcon className="w-4 h-4" />;
      case 'reviewed':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'rejected':
        return <XCircleIcon className="w-4 h-4" />;
      default:
        return <ExclamationTriangleIcon className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'requested':
        return 'bg-yellow-100 text-yellow-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'reviewed':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = requests.filter(request => {
    const searchLower = searchTerm.toLowerCase();
    const leadName = `${request.lead.firstName} ${request.lead.lastName}`.toLowerCase();
    const leadEmail = request.lead.email.toLowerCase();
    
    return leadName.includes(searchLower) || leadEmail.includes(searchLower);
  });

  if (loading) {
    return (
      <Layout title="Documents" subtitle="Loading...">
        <div className="flex items-center justify-center py-12">
          <div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading documents...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        <p>{error}</p>
        <button 
          onClick={fetchRequests}
          className="mt-4 text-blue-600 hover:underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <Layout title="Documents" subtitle="Manage document requests and submissions">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
            <p className="text-gray-600">Manage document requests and submissions</p>
          </div>
        <div className="flex space-x-3">
          <PermissionGate module="documents" permission="edit">
            <button
              onClick={() => setShowSettings(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <CogIcon className="w-4 h-4 mr-2" />
              Document Settings
            </button>
          </PermissionGate>
          <PermissionGate module="documents" permission="create">
            <button
              onClick={() => setShowDirectUploadModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <CloudArrowUpIcon className="w-4 h-4 mr-2" />
              Quick Upload
            </button>
          </PermissionGate>
          <PermissionGate module="documents" permission="create">
            <button
              onClick={() => setShowBulkManualUploadModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <UsersIcon className="w-4 h-4 mr-2" />
              Bulk Manual Upload
            </button>
          </PermissionGate>
          <PermissionGate module="documents" permission="create">
            <div className="relative">
              <button
                onClick={() => setShowRequestDropdown(!showRequestDropdown)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Request Documents
                <ChevronDownIcon className="w-4 h-4 ml-2" />
              </button>
              
              {showRequestDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowRequestDropdown(false)}
                  ></div>
                  <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowRequestModal(true);
                          setShowRequestDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <PlusIcon className="w-4 h-4 mr-3 text-gray-500" />
                        Single Lead Request
                      </button>
                      <button
                        onClick={() => {
                          setShowBulkRequestModal(true);
                          setShowRequestDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <UsersIcon className="w-4 h-4 mr-3 text-gray-500" />
                        Bulk Request (Multiple Leads)
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by lead name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="requested">Requested</option>
              <option value="submitted">Submitted</option>
              <option value="reviewed">Reviewed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-white shadow-sm border rounded-lg">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No document requests</h3>
            <p className="text-gray-600 mb-4">Get started by requesting documents from your leads.</p>
            <PermissionGate module="documents" permission="create">
              <button
                onClick={() => setShowRequestModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Request Documents
              </button>
            </PermissionGate>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredRequests.map((request) => {
              // Calculate progress for each request
              const totalItems = request.items?.length || 0;
              const submittedItems = request.items?.filter(item => item.status === 'submitted').length || 0;
              const progressPercentage = totalItems > 0 ? (submittedItems / totalItems) * 100 : 0;
              const isPartialSubmission = submittedItems > 0 && submittedItems < totalItems;
              
              return (
              <div
                key={request.id}
                className="p-6 hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedRequest(request)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-1">
                        <h3 className="text-lg font-medium text-gray-900">
                          {request.lead.firstName} {request.lead.lastName}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                          {getStatusIcon(request.status)}
                          <span className="ml-1 capitalize">{request.status}</span>
                        </span>
                        {request.isReRequest && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                            Re-Request
                          </span>
                        )}
                        {isPartialSubmission && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <ClockIcon className="w-3 h-3 mr-1" />
                            Partial
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-6 text-sm text-gray-500 mb-2">
                        <span>{request.lead.email}</span>
                        <span>Requested by {request.requestedByUser.firstName} {request.requestedByUser.lastName}</span>
                        <span>{formatDate(request.requestedAt)}</span>
                      </div>
                      
                      {/* Progress Bar */}
                      {totalItems > 0 && request.status !== 'reviewed' && request.status !== 'rejected' && (
                        <div className="flex items-center space-x-3 mt-2">
                          <div className="flex-1 max-w-xs">
                            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                              <div 
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  progressPercentage === 100 
                                    ? 'bg-gradient-to-r from-green-500 to-green-600' 
                                    : 'bg-gradient-to-r from-blue-500 to-blue-600'
                                }`}
                                style={{ width: `${progressPercentage}%` }}
                              ></div>
                            </div>
                          </div>
                          <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                            {submittedItems}/{totalItems} docs
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {(request.status === 'requested' || request.status === 'submitted') && (
                      <PermissionGate module="documents" permission="create">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRequestForUpload(request);
                            setShowManualUploadModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-blue-600"
                          title="Upload documents on behalf of client"
                        >
                          <CloudArrowUpIcon className="w-5 h-5" />
                        </button>
                      </PermissionGate>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRequest(request);
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <EyeIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

        {/* Modals */}
        {showSettings && (
          <DocumentSettingsModal
            onClose={() => setShowSettings(false)}
            onDocumentTypeUpdated={fetchRequests}
          />
        )}

        {showRequestModal && (
          <RequestDocumentsModal
            onClose={() => setShowRequestModal(false)}
            onRequestCreated={handleRequestCreated}
          />
        )}

        {showBulkRequestModal && (
          <BulkRequestDocumentsModal
            onClose={() => setShowBulkRequestModal(false)}
            onRequestCreated={handleRequestCreated}
          />
        )}

        {selectedRequest && (
          <DocumentRequestDetail
            request={selectedRequest}
            onClose={() => setSelectedRequest(null)}
            onRequestUpdated={handleRequestUpdated}
          />
        )}

        {showManualUploadModal && selectedRequestForUpload && (
          <ManualDocumentUploadModal
            request={selectedRequestForUpload}
            onClose={() => {
              setShowManualUploadModal(false);
              setSelectedRequestForUpload(null);
            }}
            onUploadSuccess={() => {
              fetchRequests();
            }}
          />
        )}

        {showBulkManualUploadModal && (
          <BulkManualDocumentUploadModal
            onClose={() => setShowBulkManualUploadModal(false)}
            onUploadSuccess={() => {
              fetchRequests();
            }}
          />
        )}

        {/* Direct Document Upload Modal */}
        <DirectDocumentUploadModal
          isOpen={showDirectUploadModal}
          onClose={() => setShowDirectUploadModal(false)}
          onSuccess={() => {
            fetchRequests();
          }}
        />
      </div>
    </Layout>
  );
};

export default Documents;
